import React, { useState, useEffect } from 'react';
import Modal from 'components/_layouts/Modal';
import { Box, Stack, Button, CircularProgress, Input } from '@mui/material';
import ContentEditor from 'components/ContentEditor';
import { enqueueSnackbar } from 'notistack';
import { translate as t } from 'hooks/useTranslations';
import { ReleaseNotes, History as ReleaseNotesHistory } from 'views/App/Portal/Policy/_types/manuals/draft';
import StatusChip from './StatusChip';
import { LocalizationProvider } from '@mui/x-date-pickers/LocalizationProvider';
import { DatePicker } from '@mui/x-date-pickers/DatePicker';
import { AdapterDayjs } from '@mui/x-date-pickers/AdapterDayjs';
import dayjs, { Dayjs } from 'dayjs';

interface ReleaseNotesModalProps {
  open: boolean;
  initialData?: ReleaseNotesHistory;
  onClose: () => void;
  onSave?: ({ data, mode }: { data: { title: string; content: string }; mode: 'EDIT' | 'CREATE' }) => Promise<void>;
  editorHeight?: number;
  manualLevel?: string;
  status?: ReleaseNotes['draftState'];
}

function formatDate(value?: Dayjs | null): string {
  const dateToFormat = value && value.isValid() ? value : dayjs();
  return dateToFormat.format('MM/DD/YYYY');
}

const ReleaseNotesModal: React.FC<ReleaseNotesModalProps> = ({
  open,
  onClose,
  onSave,
  initialData,
  editorHeight = 400,
  status = 'DRAFT',
  manualLevel,
}) => {
  const isNew = !initialData;
  const [title, setTitle] = React.useState<Dayjs | null>(
    initialData?.title ? dayjs(initialData.title, ['MM/DD/YYYY', 'YYYY-MM-DD']) : dayjs()
  );
  const [content, setContent] = useState(initialData?.contentEditable || initialData?.content || '');
  const [saving, setSaving] = useState(false);
  const [editorResetKey, setEditorResetKey] = useState(0);

  useEffect(() => {
    if (open) {
      setTitle(initialData?.title ? dayjs(initialData.title, ['MM/DD/YYYY', 'YYYY-MM-DD']) : dayjs());
      setContent(initialData?.contentEditable || initialData?.content || '');
      setEditorResetKey((prev) => prev + 1);
    }
  }, [open, initialData]);

  const handleSave = async () => {
    setSaving(true);
    try {
      if (onSave) {
        await onSave({ data: { title: formatDate(title), content }, mode: isNew ? 'CREATE' : 'EDIT' });
        enqueueSnackbar(t('draftManuals.releaseNotes.saveSuccess'), { variant: 'success' });
      }
      onClose();
    } catch (e) {
      let errorMessage = t('draftManuals.releaseNotes.saveError');
      if (e && typeof e === 'object') {
        const errorObj = e as any;
        if (errorObj.httpStatus === 409) {
          errorMessage = t('draftManuals.releaseNotes.saveErrorLocked');
        }
      }
      enqueueSnackbar(errorMessage, { variant: 'error' });
    } finally {
      setSaving(false);
    }
  };

  const handleCancel = () => {
    // Reset all state to initial values
    setTitle(initialData?.title ? dayjs(initialData.title, ['MM/DD/YYYY', 'YYYY-MM-DD']) : dayjs());
    setContent(initialData?.contentEditable || initialData?.content || '');
    setSaving(false);
    // Force SimpleEditor to reinitialize with original content
    setEditorResetKey((prev) => prev + 1);
    onClose();
  };

  return (
    <Modal
      open={open}
      onClose={handleCancel}
      title={t('draftManuals.releaseNotes.title')}
      subtitle={null}
      size='md'
      actions={
        manualLevel === 'AGENCY'
          ? []
          : [
              <Button
                key='cancel'
                variant='outlined'
                color='neutral'
                onClick={handleCancel}
                disabled={saving}
                aria-label={t('draftManuals.releaseNotes.cancelAria')}
              >
                {t('draftManuals.releaseNotes.goBackTitle')}
              </Button>,
              <Button
                key='save'
                variant='contained'
                color='primary'
                onClick={handleSave}
                disabled={saving}
                endIcon={
                  saving ? (
                    <CircularProgress
                      size={20}
                      color='inherit'
                    />
                  ) : null
                }
                aria-label={t(
                  `draftManuals.${isNew ? 'releaseNotes.createReleaseNotesAria' : 'releaseNotes.editReleaseNotesAria'}`
                )}
              >
                {t('draftManuals.guideSheet.save')}
              </Button>,
            ]
      }
    >
      <Stack
        spacing={4}
        sx={{ mt: 2 }}
      >
        <Stack
          direction='row'
          spacing={3}
          alignItems='center'
        >
          <Box sx={{ flex: 1, display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
            <LocalizationProvider dateAdapter={AdapterDayjs}>
              <DatePicker
                value={title}
                onChange={setTitle}
                format='MM/DD/YYYY'
                slotProps={{
                  textField: {
                    fullWidth: false,
                    size: 'small',
                    hiddenLabel: true,
                    sx: {
                      '&.MuiFormControl-root': {
                        paddingTop: 0,
                      },
                    },
                  },
                }}
              />
            </LocalizationProvider>
            <StatusChip
              manualLevel={manualLevel}
              draftState={status}
            />
          </Box>
        </Stack>
        <Box sx={{ minHeight: editorHeight, maxHeight: editorHeight, height: editorHeight }}>
          {open && (
            <ContentEditor
              key={`release-notes-editor-${open}-${initialData?.updatedAt || 'new'}-${editorResetKey}`}
              id='release-notes-editor'
              contentHTML={content}
              onSave={setContent}
              isEditable={manualLevel !== 'AGENCY'}
              showToolbar={true}
              excalidrawEnabled={false}
              trackChangesEnabled={false}
              textEntitiesEnabled={false}
              crossReferencesEnabled={false}
              imagesEnabled={false}
              attachmentsEnabled={false}
              maxHeight='800px'
            />
          )}
        </Box>
      </Stack>
    </Modal>
  );
};

export default ReleaseNotesModal;
